import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { VpButton, VpDropzone } from '@vp-ui';
import { VpColorPicker } from '@vp-ui/inputs/VpColorPicker/VpColorPicker';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Slider from '@mui/material/Slider';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';

import VpVideoBrandingPreview from '@vp-ui/components/VpVideoBrandingPreview/VpVideoBrandingPreview';
import VpPositionGrid from '@vp-ui/components/VpPositionGrid/VpPositionGrid';
import {
  WATERMARK_POSITIONS,
  WATERMARK_POSITION_VALUES,
  TITLE_POSITIONS,
  TITLE_POSITION_VALUES,
  CROP_RATIOS
} from '@src/constants/branding-layout';
import { formatCropLabel } from '@src/constants/crop';
import {
  useOrgPresets,
  useSavePreset,
  useDeletePreset,
  useMediaBrandingBurns,
  useStartBrandingBurn,
  useDeleteBrandingBurn,
  isBrandingDataProcessing
} from '@src/hooks/react-query/useBrandingData';
import { useFetchUploadPresignedUrl, useUploadToPresignedUrl } from '@src/hooks/react-query/useAssetUpload';
import { withRouter } from 'react-router';

import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';
import { useMediaTableContext } from '@src/hooks/contexts';
import VpMediaVersionList, { versionId } from '@vp-ui/components/VpMediaVersionList/VpMediaVersionList';

const DEFAULT_CONFIG = {
  titleEnabled: false,
  titleText: '',
  titleFontColor: '#ffffff',
  titleBackground: 'rgba(0,0,0,0.5)',
  titlePosition: TITLE_POSITIONS.TOP_CENTER,
  watermarkEnabled: false,
  watermarkStoragePath: null,
  watermarkUrl: null,
  watermarkPosition: WATERMARK_POSITIONS.BOTTOM_RIGHT,
  watermarkOpacity: 0.8,
  watermarkScale: 0.15,
  crop: '16:9'
};

/**
 * Extracts the S3 object key from a presigned upload URL.
 *
 * The API turns this path into `s3://<bucket>/<path>` for the burner, so it has to be the
 * object's real key. Reading it back off the presigned URL keeps us independent of
 * AssetUploadV1Service's naming scheme — reconstructing `video_library/<name>.<ext>` by hand
 * would break silently the day that layout changes.
 */
export const storagePathFromPresignedUrl = (presignedUrl) => {
  if (!presignedUrl) return null;

  try {
    return new URL(presignedUrl).pathname.replace(/^\//, '');
  } catch (e) {
    return null;
  }
};

/**
 * Builds the burn payload: flat camelCase, exactly the shape BrandingV1Validator accepts.
 * Never nested — nesting only happens server-side, when the job is translated into the SQS
 * message. `watermarkUrl` is preview-only state and is deliberately dropped, since an
 * unknown field makes the validator reject the whole request.
 */
export const buildBurnConfig = (state) => ({
  titleEnabled: state.titleEnabled,
  titleText: state.titleText,
  titleFontColor: state.titleFontColor,
  titleBackground: state.titleBackground,
  titlePosition: state.titlePosition,
  watermarkEnabled: state.watermarkEnabled,
  watermarkStoragePath: state.watermarkStoragePath,
  watermarkPosition: state.watermarkPosition,
  watermarkOpacity: state.watermarkOpacity,
  watermarkScale: state.watermarkScale,
  crop: state.crop
});

/**
 * Payload for creating/updating a preset.
 *
 * A preset has no `crop` column and the validator rejects unknown fields, so crop is left out
 * explicitly rather than relying on JSON.stringify silently dropping an `undefined` key.
 */
export const buildPresetPayload = (name, state) => {
  const payload = { name, ...buildBurnConfig(state) };
  delete payload.crop;
  return payload;
};

/** Newest burn, chosen explicitly rather than trusting the server's ORDER BY. */
export const latestBurnOf = (burns) => {
  const items = Array.isArray(burns) ? burns : [];
  if (items.length === 0) return null;

  return items.reduce((newest, burn) =>
    new Date(burn.createdAt || 0) > new Date(newest.createdAt || 0) ? burn : newest
  );
};

/**
 * True when any pixel is not fully opaque.
 *
 * The burn composites the logo through its alpha channel (colorchannelmixer over
 * format=rgba), so a flattened PNG — common for logos taken from the web — paints a solid
 * rectangle over the video. The mime type cannot catch that: an opaque file is still a valid
 * PNG. Missing data counts as opaque, so a read failure never silently approves a bad logo.
 */
export const hasTransparency = (rgbaData) => {
  if (!rgbaData || !rgbaData.length) return false;

  for (let i = 3; i < rgbaData.length; i += 4) {
    if (rgbaData[i] < 255) return true;
  }

  return false;
};

/**
 * Reads a file's pixels so its alpha channel can be inspected. Resolves to null when the
 * image cannot be decoded, which hasTransparency then treats as opaque.
 */
const readImagePixels = (file) =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0);
        resolve(context.getImageData(0, 0, canvas.width, canvas.height).data);
      } catch (e) {
        resolve(null);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    image.src = objectUrl;
  });

/**
 * Normalises a colour to the comma form the API's pattern and the burner's cssColorToFFmpeg
 * both parse. VpColorPicker emits CSS Color 4 syntax (`rgb(R G B / A)`, space separated),
 * which neither accepts — without this every burn using the picker would 400.
 */
export const normalizeColor = (value) => {
  if (!value) return '#000000';
  if (/^#[0-9a-fA-F]{3,6}$/.test(value)) return value;
  if (/^rgba?\(\s*\d/.test(value) && value.includes(',')) return value;

  const match = value.match(/^rgba?\(\s*(\d+)\s+(\d+)\s+(\d+)\s*(?:\/\s*([\d.]+)\s*)?\)$/);
  if (!match) return '#000000';

  const alpha = match[4] !== undefined ? match[4] : '1';
  return `rgba(${match[1]},${match[2]},${match[3]},${alpha})`;
};

/** Composes an `rgba()` string the API's colour pattern accepts. */
export const composeRgba = (hexColor, opacity) => {
  const hex = (hexColor || '#000000').replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

/**
 * Rebuilds the form from a burn's applied config, so an existing burn is visible in the
 * preview instead of the panel looking untouched. `watermarkUrl` is resolved by the API
 * (BrandingV1Service.getBurnsByMedia) for the same reason presets have it.
 */
export const burnToConfig = (appliedConfig = {}) => ({
  ...DEFAULT_CONFIG,
  titleEnabled: !!appliedConfig.titleEnabled,
  titleText: appliedConfig.titleText || '',
  titleFontColor: appliedConfig.titleFontColor || DEFAULT_CONFIG.titleFontColor,
  titleBackground: appliedConfig.titleBackground || DEFAULT_CONFIG.titleBackground,
  titlePosition: appliedConfig.titlePosition || DEFAULT_CONFIG.titlePosition,
  watermarkEnabled: !!appliedConfig.watermarkEnabled,
  watermarkStoragePath: appliedConfig.watermarkStoragePath || null,
  watermarkUrl: appliedConfig.watermarkUrl || null,
  watermarkPosition: appliedConfig.watermarkPosition || DEFAULT_CONFIG.watermarkPosition,
  watermarkOpacity: appliedConfig.watermarkOpacity ?? DEFAULT_CONFIG.watermarkOpacity,
  watermarkScale: appliedConfig.watermarkScale ?? DEFAULT_CONFIG.watermarkScale,
  crop: appliedConfig.crop || DEFAULT_CONFIG.crop
});

export const presetToConfig = (preset, currentCrop) => ({
  ...DEFAULT_CONFIG,
  // A preset carries no crop, so the one the user already picked is preserved instead of
  // being silently reset to the baseline.
  crop: currentCrop || DEFAULT_CONFIG.crop,
  // Comes straight from the API — see BrandingV1Service.withWatermarkUrl. Deriving it here
  // would mean hardcoding which bucket assets live in, which is knowledge the client does not
  // have (and got wrong: the CDN host is env-derived and empty in local dev).
  watermarkUrl: preset.watermarkUrl || null,
  titleEnabled: !!preset.titleEnabled,
  titleText: preset.titleText || '',
  titleFontColor: preset.titleFontColor || DEFAULT_CONFIG.titleFontColor,
  titleBackground: preset.titleBackground || DEFAULT_CONFIG.titleBackground,
  titlePosition: preset.titlePosition || DEFAULT_CONFIG.titlePosition,
  watermarkEnabled: !!preset.watermarkEnabled,
  watermarkStoragePath: preset.watermarkStoragePath || null,
  watermarkPosition: preset.watermarkPosition || DEFAULT_CONFIG.watermarkPosition,
  watermarkOpacity: preset.watermarkOpacity ?? DEFAULT_CONFIG.watermarkOpacity,
  watermarkScale: preset.watermarkScale ?? DEFAULT_CONFIG.watermarkScale
});

const VpMediaInfoBranding = ({ media, history }) => {
  const mediaId = media?.mediaId;
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [presetName, setPresetName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [confirmingRemoveId, setConfirmingRemoveId] = useState(null);

  const { addSnackbarError, addSnackbarSuccess } = useVpSnackbar();
  const { setSelectedSubmissionId } = useMediaTableContext();

  const {
    data: presets,
    isLoading: presetsIsLoading,
    isError: presetsIsError,
    error: presetsError
  } = useOrgPresets();
  const { mutate: savePreset } = useSavePreset();
  const { mutate: deletePreset } = useDeletePreset();
  const { data: burns } = useMediaBrandingBurns(mediaId);
  const { mutate: startBurn } = useStartBrandingBurn(mediaId);
  const { mutate: deleteBurn } = useDeleteBrandingBurn(mediaId);

  const { mutateAsync: fetchUploadPresignedUrl } = useFetchUploadPresignedUrl();
  const { mutateAsync: uploadToPresignedUrl } = useUploadToPresignedUrl();

  const versions = Array.isArray(burns) ? burns : [];
  const latestBurn = latestBurnOf(burns);
  const isBurning = isBrandingDataProcessing(burns);

  const set = (patch) => setConfig((current) => ({ ...current, ...patch }));

  // Seeds the form from the existing burn, once. Guarded by a ref rather than a dependency so
  // the 3s polling cannot keep overwriting whatever the user is typing.
  const hydratedFromBurn = useRef(false);
  useEffect(() => {
    if (hydratedFromBurn.current || !latestBurn?.appliedConfig) return;

    hydratedFromBurn.current = true;
    setConfig(burnToConfig(latestBurn.appliedConfig));
  }, [latestBurn]);

  // Mirrors the server's own rule (BrandingV1Service.resolveConfig) exactly: at least one
  // component must be enabled, AND every enabled one must be complete. The server validates
  // each independently, so "at least one is complete" is not enough — a valid title next to a
  // ticked-but-empty watermark would light the button up and then 400 on click.
  const canBurn = useMemo(() => {
    const anyEnabled = config.titleEnabled || config.watermarkEnabled;
    const titleComplete = !config.titleEnabled || !!config.titleText.trim();
    const watermarkComplete = !config.watermarkEnabled || !!config.watermarkStoragePath;

    return anyEnabled && titleComplete && watermarkComplete && !isBurning;
  }, [config, isBurning]);

  // The burner reads the logo's alpha channel (colorchannelmixer over format=rgba), so a
  // format without transparency would render an opaque rectangle over the video. VpDropzone
  // enforces the type; onDropRejected reports anything else.
  const handleLogoDrop = async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    // Warn before uploading: the burn would happily render the opaque rectangle, and the
    // user would only find out minutes later looking at the finished MP4.
    const pixels = await readImagePixels(file);
    if (!hasTransparency(pixels)) {
      addSnackbarError({
        error: new Error(
          'This PNG has no transparent areas — it will render as a solid rectangle over the video.'
        )
      });
    }

    setIsUploading(true);
    try {
      const { presignedUrl } = await fetchUploadPresignedUrl({
        mimeType: 'image/png',
        extension: 'png',
        asset_type: 'video_library'
      });

      await uploadToPresignedUrl({ presignedUrl, file });

      const storagePath = storagePathFromPresignedUrl(presignedUrl);

      set({
        watermarkStoragePath: storagePath,
        // The object's own URL (the presigned one minus its query) always resolves, including
        // locally where `cdnUrl` is built from an OUTPUT_BUCKET env var that dev machines do
        // not set. Presets have only the stored path, so those fall back to cdnUrl.
        watermarkUrl: presignedUrl.split('?')[0],
        watermarkEnabled: true
      });
    } catch (error) {
      addSnackbarError({ error });
    } finally {
      setIsUploading(false);
    }
  };

  const handleBurn = () => startBurn({ config: buildBurnConfig(config) });

  const handleSavePreset = () => {
    if (!presetName.trim()) return;

    savePreset(
      { payload: buildPresetPayload(presetName.trim(), config) },
      {
        onSuccess: () => {
          setPresetName('');
          addSnackbarSuccess({ message: 'Preset saved' });
        },
        onError: (error) => addSnackbarError({ error })
      }
    );
  };

  const handleDeletePreset = (preset) =>
    deletePreset(preset.id, {
      onSuccess: () => addSnackbarSuccess({ message: `Preset "${preset.name}" deleted` }),
      onError: (error) => addSnackbarError({ error })
    });

  // Confirmation is per row, not per tab: with several burns listed, one pending removal must
  // not be confusable with another. The id comes from the list's own resolver, so that the
  // value compared against `confirmingRemoveId` is the same one the row keyed itself with.
  const handleRemoveVersion = (version) => {
    const id = versionId(version);

    if (confirmingRemoveId !== id) {
      setConfirmingRemoveId(id);
      return;
    }

    deleteBurn(id, {
      onSuccess: () => {
        setConfirmingRemoveId(null);
        addSnackbarSuccess({ message: 'Branding removed. Delivery reverted to the original.' });
      },
      onError: (error) => {
        setConfirmingRemoveId(null);
        addSnackbarError({ error });
      }
    });
  };

  // Nothing is decided until the permission answer lands. Rendering the editor meanwhile is what
  // let a user in staging click Burn and get the raw API message: `isError` only flips after
  // react-query exhausts its retries, so the editable window was seconds long, not a flicker.
  if (presetsIsLoading) {
    return <CircularProgress size={24} />;
  }

  // Every branding route asserts VIDEO_BRANDING_ENABLED, presets included, so the presets 403 is
  // the earliest and cheapest signal that this user has no access. Without this the whole editor
  // rendered for them — they could pick a title and upload a logo, because the logo goes through
  // the generic asset upload and is not gated — and only the burn button failed, with
  // `Access to VIDEO_BRANDING_ENABLED is disabled for this user`. An internal flag name, shown
  // after all the work. The Captions tab answers the same situation this way.
  //
  // Only 403: any other failure is a blip, and blanking the panel would hide the feature from
  // people who do have it.
  if (presetsIsError && presetsError?.response?.status === 403) {
    return (
      <Stack gap={2} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          Video branding is not available on your current plan.
        </Typography>
        {/* VpButton, not `Button component={Link}`: react-router here is 4.x, whose Link is a
            class that does not forward refs, so MUI's ref lands on the instance and
            useIsFocusVisible throws on `node.ownerDocument`. VpButton wraps Link in a forwardRef
            that passes `innerRef`, which is v4's way of reaching the DOM node. */}
        <VpButton to="/billing" variant="outlined" size="small">
          Upgrade Plan
        </VpButton>
      </Stack>
    );
  }

  return (
    <Stack gap={3}>
      <VpVideoBrandingPreview
        mediaUrl={media?.mediaUrl}
        crop={config.crop}
        title={{
          enabled: config.titleEnabled,
          text: config.titleText,
          fontColor: config.titleFontColor,
          background: config.titleBackground,
          position: config.titlePosition
        }}
        watermark={{
          enabled: config.watermarkEnabled,
          url: config.watermarkUrl,
          position: config.watermarkPosition,
          opacity: config.watermarkOpacity,
          scale: config.watermarkScale
        }}
      />

      {/* ── Aspect ratio ──────────────────────────────────────────────────────
          Sits with the preview: it reframes the whole video, so it belongs to neither the
          title nor the watermark. */}
      <TextField
        select
        size="small"
        label="Aspect ratio"
        value={config.crop}
        onChange={(e) => set({ crop: e.target.value })}
        sx={{ maxWidth: 200 }}
      >
        {CROP_RATIOS.map((ratio) => (
          <MenuItem key={ratio} value={ratio}>
            {formatCropLabel(ratio)}
          </MenuItem>
        ))}
      </TextField>

      {/* ── Title and watermark, side by side ─────────────────────────────────
          Pairing them roughly halves the tab's height, which is what keeps the preview on
          screen while either one is edited — no sticky positioning needed. On a narrow dialog
          the columns collapse to one, title first. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          // `start`, deliberately — not `stretch`. Both cards hide their body behind a
          // checkbox, so equal heights make one card's frame grow when the *other* one is
          // switched on, ballooning around empty space for no reason the user can see. A
          // ragged bottom edge is the lesser evil when the content is conditional.
          alignItems: 'start'
        }}
      >
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.titleEnabled}
                  onChange={(e) => set({ titleEnabled: e.target.checked })}
                  inputProps={{ 'aria-label': 'Title' }}
                />
              }
              label="Title"
            />
            {config.titleEnabled && (
              <Stack gap={2}>
                <TextField
                  size="small"
                  label="Title text"
                  value={config.titleText}
                  inputProps={{ maxLength: 120 }}
                  onChange={(e) => set({ titleText: e.target.value })}
                />
                {/* Narrow columns make this row wrap, so `gap` is the vertical distance between
                    the two pickers as often as the horizontal one — keep it tight. */}
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption">Text colour</Typography>
                    <VpColorPicker
                      name="titleFontColor"
                      defaultValue="rgb(255 255 255 / 1)"
                      onChange={(value) => set({ titleFontColor: normalizeColor(value) })}
                    />
                  </Box>
                  {/* isRgba gives the background its alpha channel in one control, replacing
                      the separate opacity slider the first pass had. */}
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption">Background</Typography>
                    <VpColorPicker
                      name="titleBackground"
                      isRgba
                      defaultValue="rgb(0 0 0 / 0.5)"
                      onChange={(value) => set({ titleBackground: normalizeColor(value) })}
                    />
                  </Box>
                </Stack>

                <Box>
                  <Typography variant="caption">Position</Typography>
                  <VpPositionGrid
                    label="Title position"
                    value={config.titlePosition}
                    enabled={TITLE_POSITION_VALUES}
                    onChange={(position) => set({ titlePosition: position })}
                  />
                </Box>
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.watermarkEnabled}
                  onChange={(e) => set({ watermarkEnabled: e.target.checked })}
                  inputProps={{ 'aria-label': 'Watermark' }}
                />
              }
              label="Watermark"
            />
            {config.watermarkEnabled && (
              <Stack gap={2}>
                {/* `hasFile` defaults to true in VpDropzone, which renders the "file uploaded,
                    remove it to upload another" state — so it has to be driven by whether a
                    logo actually exists, or the empty dropzone claims to already hold one. */}
                <VpDropzone
                  accept={{ 'image/png': ['.png'] }}
                  title="Select a PNG logo or drag and drop here"
                  subtitle="PNG only — the burn uses the logo's transparency"
                  onDropAccepted={handleLogoDrop}
                  onDropRejected={() =>
                    addSnackbarError({ error: new Error('The watermark must be a PNG file') })
                  }
                  logoUrl={config.watermarkUrl}
                  hasFile={!!config.watermarkUrl}
                  loading={isUploading}
                />

                {/* The grid is a fixed 132px block, so the sliders take the remaining width
                    beside it instead of each claiming a row of its own. */}
                <Stack direction="row" gap={4} alignItems="flex-start" flexWrap="wrap">
                  {/* flexShrink: 0 — without it the sliders' `flex: 1` eats into the grid and
                      squashes the cells to slivers. The grid is a fixed block; the sliders are
                      what gives. */}
                  <Box sx={{ flexShrink: 0 }}>
                    <Typography variant="caption">Position</Typography>
                    <VpPositionGrid
                      label="Watermark position"
                      value={config.watermarkPosition}
                      enabled={WATERMARK_POSITION_VALUES}
                      onChange={(position) => set({ watermarkPosition: position })}
                    />
                  </Box>

                  <Stack gap={1} sx={{ flex: 1, minWidth: 120, maxWidth: 190 }}>
                    <Box>
                      <Typography variant="caption">Opacity</Typography>
                      <Slider
                        size="small"
                        min={0}
                        max={1}
                        step={0.05}
                        value={config.watermarkOpacity}
                        onChange={(_, value) => set({ watermarkOpacity: value })}
                      />
                    </Box>

                    <Box>
                      <Typography variant="caption">Size</Typography>
                      <Slider
                        size="small"
                        min={0.02}
                        max={0.5}
                        step={0.01}
                        value={config.watermarkScale}
                        onChange={(_, value) => set({ watermarkScale: value })}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Box>

      {/* ── Presets ─────────────────────────────────────────────────────────────
          Each preset is one chip: the body applies it, the icon deletes it. A saved preset is
          a short label, so a row of chips that wraps reads faster than a stack of rows and
          takes a fraction of the height — which is the whole point of this tab's layout. */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack gap={1.5}>
          <Typography variant="subtitle2">Presets</Typography>

          {(presets || []).length > 0 ? (
            <Stack direction="row" gap={1} flexWrap="wrap">
              {presets.map((preset) => (
                <Chip
                  key={preset.id}
                  label={preset.name}
                  variant="outlined"
                  onClick={() => setConfig(presetToConfig(preset, config.crop))}
                  onDelete={() => handleDeletePreset(preset)}
                  // MUI's delete affordance is an icon, and SvgIcon ships `aria-hidden="true"`
                  // by default — so naming it is not enough, it has to be put back into the
                  // accessibility tree or neither a screen reader nor a test can reach it.
                  deleteIcon={
                    <DeleteIcon
                      role="button"
                      aria-hidden={false}
                      aria-label={`Delete preset ${preset.name}`}
                    />
                  }
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Save the current title and watermark to reuse them on other videos.
            </Typography>
          )}

          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Preset name"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              sx={{ maxWidth: 220 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleSavePreset}
              disabled={!presetName.trim()}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ── Versions ──────────────────────────────────────────────────────────
          Every burn, not just the newest: the earlier ones existed in the API and were
          invisible here. The list sits with the button that produces it, at the end of the
          form — reading order matches cause and effect.

          The form above is still seeded by the most recent burn; this changed what the tab
          displays, not how it edits. */}
      {versions.length > 0 && (
        <Stack gap={1}>
          <Typography variant="subtitle2">Versions</Typography>
          <VpMediaVersionList
            versions={versions}
            mediaId={mediaId}
            actions={['download', 'viewInMediaTab', 'remove']}
            confirmingRemoveId={confirmingRemoveId}
            onRemove={handleRemoveVersion}
            onViewInMediaTab={() => {
              setSelectedSubmissionId(null);
              history.push('/media', { pendingExpandVersions: mediaId });
            }}
          />
        </Stack>
      )}

      <VpButton variant="contained" onClick={handleBurn} disabled={!canBurn}>
        Burn
      </VpButton>
    </Stack>
  );
};

VpMediaInfoBranding.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func }),
  media: PropTypes.shape({
    mediaId: PropTypes.string,
    mediaUrl: PropTypes.string
  })
};

export default withRouter(VpMediaInfoBranding);



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoBranding.jsx